import { memo, useCallback, useMemo, type FunctionComponent } from 'react';
import toSafeInteger from 'lodash/toSafeInteger';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
// Skillmore UI Components
import AccountCircleAlt from '@empathco/ui-components/src/icons/AccountCircleAlt';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import ContentCard from '@empathco/ui-components/src/elements/ContentCard';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import TruncatedTextLink from '@empathco/ui-components/src/elements/TruncatedTextLink';
import MatchIndicator from '@empathco/ui-components/src/elements/MatchIndicator';
// local imports
import useNonReducedUI from '../constants/managementLevel';
import { Employee } from '../models/employee';
import useCustomerSettings from '../config/customer';
import useModels from '../helpers/models';

type EmployeeDetailsBandProps = {
  readonly fullName?: string | null;
  readonly employee?: Employee | null;
  readonly onJobClick?: (roleId: string) => void | null;
};

const EmployeeDetailsBand: FunctionComponent<EmployeeDetailsBandProps> = ({
  fullName,
  employee,
  onJobClick
}) => {
  const { getEmployeeContactUrl } = useCustomerSettings();
  const { showNonReducedUI } = useNonReducedUI();
  const { getEmployeeLocationStr } = useModels();
  const showMatchRate = showNonReducedUI(employee);
  const { email, current_job, location, work_location } = employee || {};
  const { code, title, match_rate, location: jobLocation } = current_job || {};

  const href = useMemo(() => getEmployeeContactUrl(code, email), [code, email, getEmployeeContactUrl]);
  const locationStr = useMemo(
    () => getEmployeeLocationStr(work_location, jobLocation, location),
    [work_location, jobLocation, location, getEmployeeLocationStr]
  );

  const handleJobClick = useCallback(() => {
    if (code && onJobClick) onJobClick(code);
  }, [code, onJobClick]);

  return (
    <ContentCard pending={!employee}>
      <CardSection compact flex>
        <Box
            py={1}
            flex="1 1 auto"
            display="flex"
            alignItems="center"
            flexWrap="wrap"
        >
          <Box
              flex="1 1 auto"
              py={2}
              display="flex"
              alignItems="center"
          >
            <AccountCircleAlt fontSize="large" color="primary"/>
            <BoxTypography pl={1} flexGrow={1} variant="subtitle2">
              <TruncatedTextLink variant="inherit" maxLines={1} href={href} text={fullName} sx={{ whiteSpace: 'wrap' }}/>
            </BoxTypography>
          </Box>
          <Box sx={{ alignSelf: 'stretch', p: 1.5, visibility: { xs: 'hidden', sm: 'visible' } }}>
            <Divider
                orientation="vertical"
                variant="middle"
            />
          </Box>
          <BoxTypography
              flex="1 1 auto"
              py={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
              align="center"
              variant="body1"
          >
            {locationStr || <FormattedMessage id="employees.not_available"/>}
          </BoxTypography>
          <Box sx={{ alignSelf: 'stretch', p: 1.5, visibility: { xs: 'hidden', sm: 'visible' } }}>
            <Divider
                orientation="vertical"
                variant="middle"
            />
          </Box>
          <Box
              flex="1 1 auto"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
          >
            <Box
                flex="1 1 auto"
                py={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
            >
              <TruncatedTextLink variant="subtitle2" text={title} onClick={code && onJobClick ? handleJobClick : undefined}/>
            </Box>
            {showMatchRate ? (
              <Box pl={2} mx={{ xs: -1, sm: 0 }} display="flex" alignItems="center">
                <MatchIndicator value={toSafeInteger(match_rate)}/>
              </Box>
            ) : undefined}
          </Box>
        </Box>
      </CardSection>
    </ContentCard>
  );
};

export default memo(EmployeeDetailsBand);
